/**
 * The computer opponent.
 *
 * It is stateless: given the game as it stands, it returns the ONE next action.
 * That is what lets the same bot drive a solo game in a browser tab and a seat
 * in an online room whose player's phone died, without either of them owning a
 * bot object that could drift out of sync with the state it is playing.
 *
 * It also means the bot sets its dice aside one at a time, exactly like a person
 * does. A bot that atomically jumps from "six dice thrown" to "banked 1,150"
 * gives you nothing to read; watching it pick up the 1, then the 5, then stop
 * and think, is most of what makes an opponent feel like an opponent.
 */

import {
  canBank,
  canPiggyback,
  heldFaces,
  type Player,
  type View,
} from './engine.js';
import { allKeeps, DICE_COUNT, type Keep } from './scoring.js';

export type Difficulty = 'easy' | 'steady' | 'sharp';

export type BotMove =
  | { t: 'roll' }
  | { t: 'takeCarry' }
  | { t: 'toggle'; index: number }
  | { t: 'bank' }
  | { t: 'endTurn' };

/**
 * P(a throw of r dice scores nothing). Exact, by enumerating all 6^r throws —
 * `npm run sim` regenerates these. The 6-dice figure being 2.3% is why a fresh
 * turn is nearly free and why nobody should ever bank on hot dice.
 */
const BUST = [0, 0.6667, 0.4444, 0.2778, 0.1574, 0.0772, 0.0231];

/** E(best keep available | the throw scored). Same enumeration. */
const GAIN = [0, 75, 90, 120, 170, 245, 435];

/**
 * Bank once the turn is worth at least this much, given dice left to throw.
 *
 * These are the OPTIMAL thresholds, from value iteration over the real game —
 * not the myopic break-even that most Farkle bots use, which prices every throw
 * as if it were the last one and consequently banks far too early.
 *
 * The shape is worth reading. One die is a two-thirds chance of losing
 * everything, and yet its threshold (350) is *higher* than two dice (300),
 * because a single scoring die clears the table and hands back all six — and six
 * fresh dice are worth 602 points on their own. Six dice never bank at all: at
 * any turn total, a 2.3% risk is cheaper than stopping. A hand-tuned table
 * essentially never discovers either of those.
 */
const BANK_AT = [0, 350, 300, 450, 1050, 3100, Infinity];

/** The easy bot ignores all of that and stops as soon as it has something. */
const EASY_BANK_AT = [0, 250, 250, 300, 350, 400, 500];

/**
 * What we think a turn ends up worth if we take this keep and play on.
 *
 * One step of lookahead, which is the step that matters: it is what makes the
 * bot keep a lone 1 and throw five dice instead of scooping the 1 and the 5 for
 * 150 and throwing four. Those two choices differ by 50 points now and by about
 * 80 in expectation, and the greedy read gets it backwards.
 */
function evaluate(keep: Keep, turnPoints: number, bankAt: readonly number[]): number {
  const after = turnPoints + keep.score;
  const left = keep.remaining === 0 ? DICE_COUNT : keep.remaining;
  if (after >= bankAt[left]!) return after;
  return (1 - BUST[left]!) * (after + GAIN[left]!);
}

interface Plan {
  keep: Keep;
  /** What to do once the dice are set aside. */
  then: 'roll' | 'bank';
}

/**
 * Roughly what a turn is worth standing at `points` with `n` dice about to fly.
 *
 * The same one-step read `evaluate` uses, and deliberately so: it understates
 * both sides of the piggyback choice by ignoring what happens after a successful
 * throw, and understating both by the same shape is all a comparison needs.
 */
function throwValue(points: number, n: number): number {
  return (1 - BUST[n]!) * (points + GAIN[n]!);
}

/**
 * Should the bot pick up what the last player left, or throw its own six?
 *
 * The trade is real and not obvious. Six fresh dice are worth about 425 — a 2.3%
 * bust risk on the best expected gain in the game. Three dice carrying 1,100 are
 * worth about 880 even at a 28% risk, so that one is taken every time; two dice
 * carrying 300 are worth about 220, so that one is left alone. Points alone do
 * not decide it and dice alone do not either.
 */
function wantsCarry(view: View, difficulty: Difficulty): boolean {
  const carry = view.carry;
  if (!carry) return false;
  // Loose players see free points and take them. This is wrong about a third of
  // the time, which is the point of the setting.
  if (difficulty === 'easy') return carry.points >= 150;

  const player = view.players[view.turn];
  // A total that wins outright the moment it survives one throw is worth more
  // than any expected value: the game ends and nothing after it is priced.
  if (player && player.score + carry.points >= view.rules.target) return true;

  return throwValue(carry.points, carry.dice) > throwValue(0, DICE_COUNT);
}

/**
 * Decide the whole turn-step at once — which dice, and what to do after.
 *
 * Exported because the solo screen uses it for the "best play" hint, and a hint
 * that disagreed with what the bot would actually do would be worse than none.
 */
export function plan(view: View, difficulty: Difficulty = 'steady'): Plan | null {
  const player = view.players[view.turn];
  if (!player) return null;

  // The dice in play right now: the ones just thrown, whether or not the bot has
  // already picked them up. Dice set aside on an earlier throw are not options.
  const inPlay = view.dice.map((d, index) => ({ ...d, index })).filter((d) => d.state !== 'set');
  const faces = inPlay.map((d) => d.face);
  const keeps = allKeeps(faces);
  if (keeps.length === 0) return null;

  const bankAt = difficulty === 'easy' ? EASY_BANK_AT : BANK_AT;
  const opponents = view.players.filter((p) => p.id !== player.id);

  let best: Plan | null = null;
  let bestValue = -Infinity;

  for (const keep of keeps) {
    // `allKeeps` indexes into the in-play list; translate back to dice indices.
    const mapped: Keep = { ...keep, indices: keep.indices.map((i) => inPlay[i]!.index) };
    const after = view.turnPoints + keep.score;
    const left = keep.remaining === 0 ? DICE_COUNT : keep.remaining;

    let value = evaluate(keep, view.turnPoints, bankAt);
    let then: 'roll' | 'bank' = after >= bankAt[left]! ? 'bank' : 'roll';

    // ── the overrides, in order of how much they matter ──

    // Banking this wins the game outright. Nothing else is worth computing.
    if (player.score + after >= view.rules.target && wouldWin(player, after, opponents, view)) {
      then = 'bank';
      value = 1e9 + after;
    }
    // Not on the board yet: a bank below the threshold scores literally nothing,
    // so stopping there is strictly worse than throwing, however bad the odds.
    else if (!player.onBoard && after < view.rules.threshold) {
      then = 'roll';
      value = (1 - BUST[left]!) * (after + GAIN[left]!);
    }
    // Someone has closed the game out and this is the last lap. Banking a total
    // that still loses is the same as farkling, so chase the number that wins.
    else if (difficulty === 'sharp' && view.closerId !== null && view.closerId !== player.id) {
      const needed = Math.max(...opponents.map((p) => p.score)) + 50 - player.score;
      if (after < needed) {
        then = 'roll';
        value = (1 - BUST[left]!) * (after + GAIN[left]!);
      }
    }

    if (value > bestValue) {
      bestValue = value;
      best = { keep: mapped, then };
    }
  }

  // A little human noise, so the easy bot is not simply a worse optimum. It
  // pushes when it should stop, which is how most people actually lose.
  if (best && difficulty === 'easy' && best.then === 'bank' && player.onBoard) {
    const left = best.keep.remaining === 0 ? DICE_COUNT : best.keep.remaining;
    if (left >= 3 && hash(view.throwId, player.id) % 4 === 0) best = { ...best, then: 'roll' };
  }

  return best;
}

/** Would banking `after` actually end the game in this player's favour? */
function wouldWin(player: Player, after: number, opponents: Player[], view: View): boolean {
  const total = player.score + after;
  if (total < view.rules.target) return false;
  // With a final round, reaching the target only starts the last lap — worth
  // taking, but the bot should not treat it as a guaranteed win when someone
  // behind it still gets a turn and could pass this total.
  if (view.rules.finalRound && view.closerId === null) return true;
  return opponents.every((p) => p.score < total);
}

/** Deterministic per-throw jitter. A bot that re-rolls its own dither looks broken. */
function hash(throwId: number, id: string): number {
  let h = throwId * 2654435761;
  for (let i = 0; i < id.length; i++) h = (Math.imul(h, 31) + id.charCodeAt(i)) | 0;
  return Math.abs(h);
}

/**
 * The next single action for the seat on turn, or null if the bot has nothing to
 * do (it is not this seat's turn, or the game is over).
 *
 * The caller paces these. The engine has no timers, so nothing here forces a
 * table to move faster than a person can follow.
 */
export function botMove(view: View, difficulty: Difficulty = 'steady'): BotMove | null {
  if (view.phase === 'over') return null;
  const player = view.players[view.turn];
  if (!player) return null;

  if (view.phase === 'idle') {
    if (canPiggyback({ ...view, rng: 0 }) && wantsCarry(view, difficulty)) return { t: 'takeCarry' };
    return { t: 'roll' };
  }
  if (view.phase === 'farkle') return { t: 'endTurn' };

  const chosen = plan(view, difficulty);
  if (!chosen) return null;

  const want = new Set(chosen.keep.indices);
  // Put down anything picked up that is not in the plan. This only fires if a
  // human's half-made selection was handed over mid-turn, but that is exactly
  // when a bot that could not correct course would wedge the table.
  for (let i = 0; i < view.dice.length; i++) {
    if (view.dice[i]!.state === 'held' && !want.has(i)) return { t: 'toggle', index: i };
  }
  // Then pick up the plan, one die at a time.
  for (const i of chosen.keep.indices) {
    if (view.dice[i]!.state === 'live') return { t: 'toggle', index: i };
  }

  // Selection matches the plan. Commit it.
  if (chosen.then === 'bank' && canBank({ ...view, rng: 0 })) return { t: 'bank' };
  if (heldFaces({ ...view, rng: 0 }).length === 0) return { t: 'roll' };
  return { t: 'roll' };
}

/** Table names, so you are not beaten by "Bot 3". */
export const BOT_NAMES = [
  'Ruby',
  'Deke',
  'Marlene',
  'Cass',
  'Otis',
  'Wanda',
  'Sy',
];
